<template lang="pug">
  li(
    :class="clss"
    @mouseover="hover"
    @mouseleave="blur"
  )
    .tree-item
      template(v-if="search")
        button.search-item(@click.stop.prevent="openSearchModal")
          //- i.fas.fa-search
          svg(xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor")
            path(stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z")
        a.search-item.hidden(type="button" href="#" @click.stop.prevent="openSearchModal")
          span.label {{ item.title }}
      template(v-else)
        a(
          type="button"
          :href="href"
          :title="item.title"
          :target="target"
          v-touch:tap="trigger"
          @keyup.enter.space="trigger"
          @click.prevent.stop
          @mouseover="focus"
          @mouseleave="leave"
        )
          img.ancor(v-if="( ! maxDepth || depth > 1)" src="@/assets/anker.svg")
          span.title {{ item.title }}
          font-awesome-icon.down(v-if="children.length && ( ! maxDepth || depth > 0)" icon="chevron-down")

          button(
            v-if="toggleEvent === 'click' && children && children.length && ( ! maxDepth || depth < maxDepth)"
            @click="toggle"
          )
            i.is-open(v-if="active")
            i.is-closed(v-else)
    TreeMenu(
      v-if="children.length && ( ! maxDepth || depth < maxDepth)"
      :items="children"
      :depth="depth + 1"
      :max-depth="maxDepth"
      :toggle-event="toggleEvent"
      :active-ids="activeIds"
      :show="active"
    )

    portal(to="modals")
      Modal.search(
        v-if="showSearchModal"
        size="sm"
        @dismiss="showSearchModal = false"
        @close="showSearchModal = false"
      )
        //- .p-4.bg-gray-200.rounded
        form.relative.flex.w-full.flex-wrap.items-stretch.mb-3(@submit.prevent.stop="submitSearch")
          input.search-input(type="text" placeholder="suchen..." ref="input" v-model="searchInput")
          span.input-icon.absolute.text-center.items-center.justify-center
            i.fas.fa-search


</template>

<script>
import { get } from '@/helpers';
import router from '@/router';

import { mapGetters, mapActions } from 'vuex';

import Modal from '@/components/Modal';

export default {
    name: 'TreeMenuItem',
    data() {
        return {
            timeout: undefined,
            visible: false,
            mouseover: false,
            showSearchModal: false,
            searchInput: ''
        };
    },
    props: {
        item: {
            type: Object,
            required: true
        },
        depth: {
            type: Number,
            required: true
        },
        maxDepth: {
            type: Number
        },
        toggleEvent: {
            type: String,
            required: true
        },
        current: {
            type: Object
        },
        activeIds: {
            type: Array
        },
        delay: {
            type: Number,
            required: true
        },
        allowMultipleActiveItems: {
            type: Boolean,
            required: true
        },
        fullWidthThreshold: {
            type: Number
        },
        font: {
            type: String
        }
    },
    components: {
        Modal
    },
    computed: {
        active() {
            if (this.allowMultipleActiveItems === true) return this.visible;
            return this.visible && this.current && this.item.id === this.current.id;
        },
        focused() {
            if (this.allowMultipleActiveItems === true) return this.mouseover;
            return (this.mouseover || this.active) && this.current && this.item.id === this.current.id;
        },
        folder() {
            const view = get(this.item, 'page.view');
            if (view === 'Folder') return true;
            return false;
        },
        search() {
            const view = get(this.item, 'page.view');
            if (view === 'Search') return true;
            return false;
        },
        redirect() {
            const view = get(this.item, 'page.view');
            if (view === 'Redirect' && this.hyperlink.type !== 'internal' && this.hyperlink.href) return true;
            return false;
        },
        hyperlink() {
            return get(this.item, 'page.hyperlink') || {};
        },
        href() {
            let href = '#';

            if (this.redirect) {
                const link = this.hyperlink.href;
                switch (this.hyperlink.type) {
                    case 'external':
                        if (!link.startsWith('https://') && !link.startsWith('http://')) href = `https://${link}`;
                        else href = link;
                        break;

                    case 'mailto':
                        href = `mailto:${link}`;
                        break;

                    case 'tel':
                        href = `tel:${link}`;
                        break;
                }
            } else if (!this.folder && this.item && this.item.path) {
                href = this.item.path;
            }

            return href;
        },
        target() {
            let target = '_self';
            if (this.hyperlink.type === 'external') {
                target = get(this.hyperlink, 'target') || '_self';
            }
            return target;
        },
        children() {
            let children = [];
            if (this.item.children && this.item.children.length) {
                children = this.item.children.filter(item => {
                    let visible = true;

                    if (item.restriction === 'members' && !this.isAuthenticated) visible = false;
                    else if (item.restriction === 'guests' && this.isAuthenticated) visible = false;
                    if (item.disabled) visible = false;

                    return visible;
                });
            }

            return children;
        },
        clss() {
            let value = [];

            if (this.$route.path === this.item.path) value.push('active');
            else if (this.activeIds && this.activeIds.length && this.activeIds.includes(this.item.id))
                value.push('active-parent');

            if (this.active) value.push('visible');
            if (this.focused) value.push('hover');
            if (this.children.length) value.push('has-children');
            if (this.depth === 1 && this.children.length >= this.fullWidthThreshold) value.push('full-width');
            if (this.search) value.push('search');

            return value;
        },
        ...mapGetters({
            isAuthenticated: 'auth/isAuthenticated'
        })
    },
    methods: {
        hover() {
            if (this.timeout) clearTimeout(this.timeout);
            if (this.toggleEvent === 'hover') {
                this.$emit('open', this.item);
                this.visible = true;
            }
        },
        blur() {
            this.timeout = setTimeout(() => {
                if (this.toggleEvent === 'hover') {
                    this.$emit('close', this.item);
                    this.visible = false;
                }
            }, this.delay);
        },
        focus() {
            if (this.toggleEvent === 'hover') {
                this.$emit('focus', this.item);
                this.mouseover = true;
            }
        },
        leave() {
            if (this.toggleEvent === 'hover') {
                this.$emit('leave', this.item);
                this.mouseover = false;
            }
        },
        open(force = false) {
            if (this.toggleEvent === 'click' || force) {
                this.$emit('open', this.item);
                this.visible = true;
            }
        },
        close() {
            if (this.toggleEvent === 'click') {
                this.$emit('close', this.item);
                this.visible = false;
            }
        },
        toggle() {
            if (this.toggleEvent === 'click') {
                if (this.active) {
                    this.$emit('close', this.item);
                    this.visible = false;
                } else {
                    this.$emit('open', this.item);
                    this.visible = true;
                }
            }
        },
        trigger(event) {
            if (event.type === 'mouseup' && event.which !== 1) return;

            if (this.item.page.view === 'Logout') {
                this.signout();
            } else if (this.folder) {
                this.toggle();
            } else {
                let next = true;
                if (
                    this.children &&
                    this.children.length &&
                    !this.active &&
                    (!this.maxDepth || this.depth < this.maxDepth)
                ) {
                    next = false;
                    this.open(true);
                }

                if (next) {
                    if (this.redirect) {
                        const win = window.open(this.href, this.target);
                        win.focus();
                    } else {
                        router.push(this.item.path).catch(error => {
                            if (error.code !== 'MODULE_NOT_FOUND') console.error(error);
                        });
                    }
                }
            }
        },
        openSearchModal() {
            this.showSearchModal = true;
            setTimeout(() => {
                this.$refs.input.focus();
            }, 150);
        },
        submitSearch() {
            router.push({ path: this.item.path, query: { s: this.searchInput } }).catch(error => {
                if (error.code !== 'MODULE_NOT_FOUND') console.error(error);
            });
            this.showSearchModal = false;
        },
        ...mapActions({
            signout: 'auth/signout'
        })
    }
};
</script>

<style lang="sass" scoped>
input.search-input
  padding: 2rem
a.hidden
  @apply hidden #{!important}
</style>
